import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDIconButton from '../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDIcon from '../../../BaseComponents/Icon/EDIcon';
import { CommonPropsAndTypes } from '../../../BaseComponents/_Types/StandardComponentProps';
import EDFab from '../../../BaseComponents/Fab/EDFab';
import { makeStyles } from 'tss-react/mui';
import EDTooltip from '../../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../../BaseComponents/Typography/EDTypography';
import { EDSpeedDial } from '../../../BaseComponents/SpeedDial/EDSpeedDial';
import { EDSpeedDialAction } from '../../../BaseComponents/SpeedDial/EDSpeedDialAction';
import EDBox from '../../../BaseComponents/EDBox/EDBox';
import classNames from 'classnames';
import { useIsTouchDevice } from '../../BreakpointContext/BreakpointContextUtils';

import type { JSX } from 'react';
import {
  FLOATING_MENU_CLEARANCE,
  FLOATING_MENU_SAFE_AREA_PADDING_RIGHT,
  useIsFloatingMenuVisible,
} from '../../FloatingBottomArea/FloatingMenu';

interface IProps {
  zoomIn: () => void;
  zoomOut: () => void;
  isAtDefault: () => boolean;
  defaultZoom: () => void;
  toggleCenteredImage: () => void;
  toggleFullScreen: () => void;
  fullScreen: boolean;
  imageCount: number;
  imageIndex: number;
  nextImage?: () => void;
  previousImage?: () => void;
  color?: CommonPropsAndTypes.ColorType;
  showCenteredImageToggle?: boolean;
  noFullscreenToggle?: boolean;
  noImageCount?: boolean;
  rotateRight: () => void;
  rotateLeft: () => void;
  ignoreFloatingMenu?: boolean;
  classes?: any;
}

const styles = (theme: Theme) => ({
  controlsContainer: {
    display: 'flex',
    flex: '0 1 auto',
    alignSelf: 'flex-end',
    alignItems: 'center',
    alignContent: 'center',
    flexDirection: 'column',
    paddingRight: theme.spacing(1),
  },
  fullscreenContainer: {
    alignSelf: 'center',
  },
  countController: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fabButton: {
    paddingBottom: theme.spacing(2),
  },
});

interface INavProps {
  onClick?: () => void;
  imageCount: number;
  imageIndex: number;
  header: string;
  headerEttGenus?: boolean;
  direction: 'next' | 'prev';
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    height: '100%',
  },
  navbarContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '55px',
    zIndex: 100,
    [theme.breakpoints.only('xs')]: {
      top: 'unset',
      bottom: theme.spacing(3.125),
    },
  },
  navbarContainerLeft: {
    left: theme.spacing(5.5),
    pointerEvents: 'none',
    [theme.breakpoints.only('xs')]: {
      left: theme.spacing(11),
    },
  },
  navbarContainerRight: {
    right: theme.spacing(5.5),
    pointerEvents: 'none',
    [theme.breakpoints.only('xs')]: {
      right: theme.spacing(11),
    },
  },
  button: {
    margin: '0px',
    padding: '0px',
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    [theme.breakpoints.only('xs')]: {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  buttonDisabled: {
    [theme.breakpoints.only('xs')]: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
}));

export const ZoomableImageNavigation: React.FC<
  React.PropsWithChildren<INavProps>
> = props => {
  const { classes, cx } = useStyles();
  const { onClick, imageCount, imageIndex } = props;

  const disabled =
    props.direction === 'prev'
      ? imageIndex === 0
      : imageIndex >= imageCount - 1;

  const prevNextPrefix = props.headerEttGenus ? 'Inget' : 'Ingen';
  const disabledReason =
    props.direction === 'prev'
      ? `${prevNextPrefix} tidigare ${props.header}`
      : `${prevNextPrefix} nästa ${props.header}`;

  return (
    <div
      className={cx(classes.navbarContainer, {
        [classes.navbarContainerLeft]: props.direction === 'prev',
        [classes.navbarContainerRight]: props.direction === 'next',
      })}
    >
      <div className={classes.container}>
        <EDIconButton
          iconSize="medium"
          className={classNames(classes.button, {
            [classes.buttonDisabled]: disabled,
          })}
          buttonTooltipSpan
          tooltip={props.direction === 'prev' ? 'Föregående' : 'Nästa'}
          icon={
            <EDIcon
              size="special"
              iconType={
                props.direction === 'prev' ? 'chevron_left' : 'chevron_right'
              }
              color={disabled ? 'disabled' : 'white'}
            />
          }
          onClick={onClick}
          disabled={disabled}
          disabledReason={disabledReason}
        />
      </div>
    </div>
  );
};

interface IHeaderProps {
  count: number;
  index: number;
  header: string;
}

const useHeaderStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  navbarContainer: {
    position: 'absolute',
    top: '10px',
    left: '35px',
    zIndex: 100,
  },
  text: {
    fontSize: '1.2em',
    color: theme.palette.common.black,
    opacity: 0.7,
  },
}));

export const ZoomableImageHeader: React.FC<
  React.PropsWithChildren<IHeaderProps>
> = props => {
  const { classes } = useHeaderStyles();
  return (
    <div className={classes.navbarContainer}>
      <EDTypography bold className={classes.text}>{`(${props.header} ${
        props.index + 1
      } / ${props.count})`}</EDTypography>
    </div>
  );
};

interface IZoomableImageAction {
  onClick: () => void;
  icon: JSX.Element;
  name?: string;
  disabled?: boolean;
}

const ZoomableImageToolbar: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const {
    defaultZoom,
    isAtDefault,
    rotateLeft,
    rotateRight,
    toggleCenteredImage,
    zoomIn,
    zoomOut,
  } = props;
  const buttonBaseColor = 'primary'; // props.color;
  const isTouchDevice = useIsTouchDevice();
  const isFloatingMenuVisible =
    useIsFloatingMenuVisible() && !props.ignoreFloatingMenu;

  const hideZoomButtons = isTouchDevice;
  const actions: IZoomableImageAction[] = React.useMemo(() => {
    const actions: IZoomableImageAction[] = [
      {
        icon: <EDIcon color="white" iconType="rotate_90_degrees_cw" />,
        onClick: rotateRight,
        name: 'Rotera höger',
      },
      {
        icon: <EDIcon color="white" iconType="rotate_90_degrees_ccw" />,
        onClick: rotateLeft,
        name: 'Rotera vänster',
      },
    ];

    if (!isTouchDevice) {
      actions.push(
        {
          icon: <EDIcon color="white" iconType="zoom_in" />,
          onClick: zoomIn,
          name: 'Zooma in',
        },
        {
          icon: <EDIcon color="white" iconType="zoom_out" />,
          onClick: zoomOut,
          name: 'Zooma ut',
        }
      );
    }
    actions.push({
      icon: <EDIcon color="white" iconType="zoom_reset" />,
      disabled: isAtDefault(),
      onClick: defaultZoom,
      name: isAtDefault() ? 'Bild redan i standardstorlek' : 'Standardstorlek',
    });

    if (props.showCenteredImageToggle) {
      actions.push({
        icon: <EDIcon color="white" iconType="aspect_ratio" />,
        onClick: toggleCenteredImage,
        name: 'Justera bildförhållande',
      });
    }

    return actions;
  }, [
    isTouchDevice,
    defaultZoom,
    isAtDefault,
    props.showCenteredImageToggle,
    rotateLeft,
    rotateRight,
    toggleCenteredImage,
    zoomIn,
    zoomOut,
  ]);

  if (isTouchDevice) {
    return (
      <EDSpeedDial
        ariaLabel={'Image toolbar'}
        icon={<EDIcon iconType="rotate_image" color="inherit" />}
        sx={{
          marginRight: isFloatingMenuVisible
            ? FLOATING_MENU_SAFE_AREA_PADDING_RIGHT
            : 1,
          marginBottom: isFloatingMenuVisible ? FLOATING_MENU_CLEARANCE : 0,
        }}
        glass={isFloatingMenuVisible}
      >
        {actions
          .slice()
          .reverse()
          .map(action => (
            <EDSpeedDialAction
              sx={{
                backgroundColor: theme => theme.palette.primary.main,
                ':hover': {
                  backgroundColor: theme => theme.palette.primary.dark,
                },
              }}
              icon={action.icon}
              key={action.name}
              tooltipTitle={action.name}
              onClick={action.onClick}
              glass={isFloatingMenuVisible}
              disabled={action.disabled}
            />
          ))}
      </EDSpeedDial>
    );
  } else {
    return (
      <EDBox sx={{ pointerEvents: 'auto' }}>
        {!hideZoomButtons &&
          actions.map(action => (
            <EDBox className={props.classes.fabButton} key={action.name}>
              <EDTooltip title={action.name}>
                <EDBox component="span">
                  <EDFab
                    size="small"
                    color={buttonBaseColor}
                    onClick={action.onClick}
                    key={action.name}
                    disabled={action.disabled}
                  >
                    {action.icon}
                  </EDFab>
                </EDBox>
              </EDTooltip>
            </EDBox>
          ))}
      </EDBox>
    );
  }
};

export default withStyles(ZoomableImageToolbar, styles);
